import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Check,
  CheckCircle2,
  Loader2,
  Pencil,
  Star,
  Trophy,
  User,
  X,
} from "lucide-react";
import ProfileSectionCard from "./ProfileSectionCard";
import FavoriteEntityPicker from "./FavoriteEntityPicker";
import { fetchDashboardProfile } from "../../redux/dashboardSlice";
import { updateFavorite } from "../../redux/profileSlice";
import {
  favoriteDraftsFromUser,
  favoriteFieldMeta,
  favoritePayloadFromDraft,
  hasFavoritePayload,
  isUserVerified,
  PROFILE_FAVORITE_FIELDS,
} from "../../utils/profileHelpers";

const favoriteIcons = {
  favoritePlayer: User,
  favoriteTeam: Star,
  favoriteLeague: Trophy,
};

const emptyEditMode = () => ({
  favoritePlayer: false,
  favoriteTeam: false,
  favoriteLeague: false,
  all: false,
});

const ProfileFavoritesPanel = ({ user, userId, onNotify }) => {
  const dispatch = useDispatch();
  const favoritesStatus = useSelector((s) => s.profile.favoritesStatus);
  const saving = favoritesStatus === "loading";

  const [editMode, setEditMode] = useState(emptyEditMode);
  const [drafts, setDrafts] = useState(() => favoriteDraftsFromUser(user));

  useEffect(() => {
    setDrafts(favoriteDraftsFromUser(user));
  }, [user]);

  if (!user) return null;

  const resetDraftField = (key) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: favoriteDraftsFromUser(user)[key],
    }));
  };

  const beginEdit = (key) => {
    setEditMode({ ...emptyEditMode(), [key]: true });
    resetDraftField(key);
  };

  const cancelEdit = (key) => {
    resetDraftField(key);
    setEditMode((prev) => ({ ...prev, [key]: false }));
  };

  const toggleEditAll = () => {
    if (editMode.all) {
      setDrafts(favoriteDraftsFromUser(user));
      setEditMode(emptyEditMode());
      return;
    }
    setDrafts(favoriteDraftsFromUser(user));
    setEditMode({ ...emptyEditMode(), all: true });
  };

  const buildPayload = (keys) =>
    Object.fromEntries(
      keys.map((key) => [key, favoritePayloadFromDraft(drafts[key])]).filter(([, value]) => value != null)
    );

  const persist = async (payload) => {
    if (!userId) {
      onNotify?.("error", "Save failed", "Sign in again to update favorites.");
      return false;
    }

    if (!isUserVerified(user)) {
      onNotify?.(
        "error",
        "Account not verified",
        "Verify your account before updating favorites."
      );
      return false;
    }

    if (!hasFavoritePayload(payload)) {
      onNotify?.("error", "Nothing to save", "Pick at least one favorite from the catalog.");
      return false;
    }

    const result = await dispatch(updateFavorite({ id: userId, ...payload }));

    if (updateFavorite.fulfilled.match(result)) {
      await dispatch(fetchDashboardProfile(userId));
      onNotify?.(
        "success",
        "Saved",
        result.payload?.message || "Favorites updated successfully."
      );
      setEditMode(emptyEditMode());
      return true;
    }

    onNotify?.("error", "Save failed", String(result.payload || "Try again."));
    return false;
  };

  const handleSaveField = (key) => persist(buildPayload([key]));

  const handleSaveAll = () =>
    persist(
      buildPayload(["favoritePlayer", "favoriteTeam", "favoriteLeague"])
    );

  const leagueFilterId = drafts.favoriteLeague?.entityId || user.favoriteLeague?.entityId || null;
  const teamFilterId = drafts.favoriteTeam?.entityId || user.favoriteTeam?.entityId || null;

  const editAllTrailing = !editMode.all ? (
    <button
      type="button"
      className="prof-text-btn"
      onClick={toggleEditAll}
      disabled={saving}
    >
      <Pencil size={11} aria-hidden />
      Edit all
    </button>
  ) : null;

  const renderFavoriteValue = (favorite) => (
    <div className="prof-fav-display">
      {favorite?.logo ? (
        <img src={favorite.logo} alt="" className="prof-fav-display-logo" />
      ) : null}
      <div className="prof-fav-display-copy">
        <p className="prof-field-value">{favorite?.name || "—"}</p>
        <span className="prof-field-meta">{favoriteFieldMeta(favorite)}</span>
      </div>
    </div>
  );

  return (
    <ProfileSectionCard
      id="favorites"
      eyebrow="Personalise"
      title="Favorites"
      icon={Star}
      trailing={editAllTrailing}
    >
      {editMode.all ? (
        <div className="prof-edit-all">
          {PROFILE_FAVORITE_FIELDS.map(({ key, label }) => {
            const Icon = favoriteIcons[key] || Star;
            return (
              <div className="prof-edit-all-row prof-edit-all-row-picker" key={key}>
                <span className="prof-field-label">
                  <span className="prof-field-icon" aria-hidden>
                    <Icon size={11} />
                  </span>
                  <em>{label}</em>
                </span>
                <FavoriteEntityPicker
                  fieldKey={key}
                  label={label}
                  value={drafts[key]}
                  onChange={(item) =>
                    setDrafts((prev) => ({ ...prev, [key]: item }))
                  }
                  disabled={saving}
                  leagueFilterId={key !== "favoriteLeague" ? leagueFilterId : null}
                  teamFilterId={key === "favoritePlayer" ? teamFilterId : null}
                />
              </div>
            );
          })}
          <div className="prof-edit-all-actions">
            <button
              type="button"
              className="prof-chip-btn prof-chip-btn-muted"
              onClick={toggleEditAll}
              disabled={saving}
            >
              <X size={11} aria-hidden />
              Cancel
            </button>
            <button
              type="button"
              className="prof-chip-btn prof-chip-btn-primary"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Save all
            </button>
          </div>
        </div>
      ) : null}

      {PROFILE_FAVORITE_FIELDS.map(({ key, label }) => {
        const favorite = user[key];
        const Icon = favoriteIcons[key] || Star;
        const verified = favorite?.status === "verified";
        const isEditing = editMode[key];

        if (editMode.all) return null;

        return (
          <div className={`prof-field${isEditing ? " prof-field-editing" : ""}`} key={key}>
            <div className="prof-field-head">
              <div className="prof-field-label">
                <span className="prof-field-icon" aria-hidden>
                  <Icon size={11} />
                </span>
                <em>{label}</em>
              </div>
              <div className="prof-field-actions">
                {verified && !isEditing ? (
                  <span className="prof-chip prof-chip-success">
                    <CheckCircle2 size={10} aria-hidden />
                    Verified
                  </span>
                ) : null}
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="prof-chip-btn prof-chip-btn-muted"
                      onClick={() => cancelEdit(key)}
                      disabled={saving}
                      aria-label={`Cancel ${label}`}
                    >
                      <X size={11} />
                    </button>
                    <button
                      type="button"
                      className="prof-chip-btn prof-chip-btn-primary"
                      onClick={() => handleSaveField(key)}
                      disabled={saving || !drafts[key]?.entityId}
                    >
                      {saving ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Check size={11} />
                      )}
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="prof-chip-btn prof-chip-btn-outline"
                    onClick={() => beginEdit(key)}
                    disabled={saving}
                  >
                    <Pencil size={10} />
                    Edit
                  </button>
                )}
              </div>
            </div>

            {isEditing ? (
              <FavoriteEntityPicker
                fieldKey={key}
                label={label}
                value={drafts[key]}
                onChange={(item) =>
                  setDrafts((prev) => ({ ...prev, [key]: item }))
                }
                disabled={saving}
                leagueFilterId={key !== "favoriteLeague" ? leagueFilterId : null}
                teamFilterId={key === "favoritePlayer" ? teamFilterId : null}
              />
            ) : (
              renderFavoriteValue(favorite)
            )}
          </div>
        );
      })}
    </ProfileSectionCard>
  );
};

export default ProfileFavoritesPanel;
